// Story 7.33 (ADO-2026): the plan a shop orders its shelf from (Figma "Planograms", desktop
// 6918:129153, tablet 7053:21718, mobile 7054:165663).
//
//   /planograms?format=&category=          steps 2 and 3: pick a Format and a Category, then a
//                                          layout from the grid of the shop's plans.
//   /planograms/:planogramId?format=&...   steps 4 and 5: the plan image over its products, the
//                    ...&node=             ordinary catalogue listing under the `planogram` scope,
//                                          so the filter rail, paging and Add are the catalogue's
//                                          own and adding keeps the shopper on the plan.
//
// The Format and Category ride in the query string (and the plan in the path), so a reload, Back
// and a pasted link land on the same step. A Department or Category picked in the plan view's rail,
// chip or breadcrumb narrows within the plan, and that node rides in the query string too, as
// Story 7.35's `node` on `/products` does (D-7.33-K): the same UUID rule and lower-case form, a
// pick or a removal is a history entry, a sanitising rewrite replaces. The facets and page within
// the plan stay in the listing's own state: the URL contract for those is `/products`'.
// Which plans the shop sees is the server's decision (its fascia, its range, SL1.0's visibility
// rule); nothing here sends a fascia.

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Banner, Icon, Modal, Select, useHeaderLayout } from '../../design-system';
import {
  fetchPlanogram,
  fetchPlanogramFilters,
  fetchPlanograms,
  planogramLayoutLabel,
  planogramOptionLabel,
  type Planogram,
  type PlanogramFilters,
} from '../../api/planogram.client';
import { isAdminRole } from '../../auth/rbac';
import { useSessionStore } from '../../auth/useSessionStore';
import type { CategoryNode } from '../../api/browse.client';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { parseNodeParam, PRODUCTS_PARAMS } from '../Products/productsUrlState';
import { CHOOSE_ROUTE } from '../../ordering/orderingChoices';
import {
  PLANOGRAMS_ROUTE,
  PLANOGRAM_NAV,
  planogramSearch,
} from '../../planogram/usePlanogramAvailability';
import { CategoryListing } from '../Browse/CategoryListing';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import { PlanogramImage } from './PlanogramImage';
import pageStyles from '../Products/Products.module.css';
import styles from './Planograms.module.css';

/** SL1.0's prompt until both filters are chosen (`displayPlanogramHome`), in the Figma's words. */
export const SELECT_PROMPT = 'Please select a format and a category';

/**
 * With no active ordering entry there is no range to check plans against, so nothing is asked:
 * the page says so and links to the choice screen instead of waiting on a read that never starts.
 */
export const NO_ENTRY_MESSAGE = 'Choose a depot and account to see its planograms.';

/** The whole plan's empty state: nothing on it is in the shop's range. */
const PLAN_EMPTY_COPY = Object.freeze({
  title: 'None of the products on this planogram are in your range.',
  hint: 'Try another layout, or check back once the range is updated.',
});

type Load<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly value: T };

/** Load `read` whenever `key` changes; a stale answer never overwrites a newer one. */
function useLoad<T>(key: string | null, read: () => Promise<T>): Load<T> {
  const [state, setState] = useState<{ key: string | null; load: Load<T> }>({
    key: null,
    load: { status: 'loading' },
  });
  useEffect(() => {
    if (key === null) return undefined;
    let cancelled = false;
    read().then(
      (value) => {
        if (!cancelled) setState({ key, load: { status: 'ready', value } });
      },
      () => {
        if (!cancelled) setState({ key, load: { status: 'error' } });
      },
    );
    return () => {
      cancelled = true;
    };
    // `read` closes over the inputs `key` already names.
  }, [key]);
  return state.key === key ? state.load : { status: 'loading' };
}

export function Planograms() {
  const { planogramId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const format = searchParams.get('format')?.trim() ?? '';
  const category = searchParams.get('category')?.trim() ?? '';
  const navigate = useNavigate();
  const entry = useCatalogueEntryKey();

  // The node the shopper narrowed the plan to (D-7.33-K), read as `/products` reads it: a `node`
  // that is not a UUID is dropped, an upper-case one is lower-cased, a repeated one is its first.
  const nodeValues = searchParams.getAll(PRODUCTS_PARAMS.node);
  const urlNode = parseNodeParam(searchParams.get(PRODUCTS_PARAMS.node));
  const nodeCanonical =
    urlNode === null
      ? nodeValues.length === 0
      : nodeValues.length === 1 && nodeValues[0] === urlNode;
  const onPlan = planogramId !== undefined;
  // Another ordering entry opens the plan whole: the view is keyed by entry and plan, so its node
  // starts afresh, and the URL drops the old entry's node with a replace. Until that write lands
  // the new entry is asked for the whole plan. An entry arriving for the first time (the session
  // resolving under a pasted link) is not a switch and keeps the link's node.
  const [entryOfNode, setEntryOfNode] = useState(entry);
  const entrySwitchPending = entryOfNode !== null && entryOfNode !== entry;
  const planNode = entrySwitchPending ? null : urlNode;

  // A node the URL names in a form other than its canonical one is rewritten with a replace, so a
  // hand-edited link leaves no Back step (Story 7.35, Decisions 8 and 10).
  useEffect(() => {
    if (!onPlan || nodeCanonical) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(PRODUCTS_PARAMS.node);
        if (urlNode !== null) next.set(PRODUCTS_PARAMS.node, urlNode);
        return next;
      },
      { replace: true },
    );
  }, [onPlan, nodeCanonical, urlNode, setSearchParams]);

  useEffect(() => {
    if (entryOfNode === entry) return;
    if (entrySwitchPending && onPlan && nodeValues.length > 0) {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete(PRODUCTS_PARAMS.node);
          return next;
        },
        { replace: true },
      );
      return;
    }
    setEntryOfNode(entry);
    // `nodeValues.length` re-runs this once the cleaned URL is in, to adopt the new entry.
  }, [entry, entryOfNode, entrySwitchPending, onPlan, nodeValues.length, setSearchParams]);

  // A pick in the rail, the chip's remove or the plan's crumb is the shopper's own move, so it is
  // a history entry, as a drill on `/products` is; `null` is the whole plan. The Format and
  // Category stay as they are.
  const narrowPlan = useCallback(
    (nodeId: string | null) => {
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (nodeId === null) next.delete(PRODUCTS_PARAMS.node);
        else next.set(PRODUCTS_PARAMS.node, nodeId);
        return next;
      });
    },
    [setSearchParams],
  );
  const role = useSessionStore((s) => s.role);
  const phoneLayout = useHeaderLayout() === 'mobile';

  const filters = useLoad<PlanogramFilters>(
    entry === null ? null : JSON.stringify([entry, format]),
    () => fetchPlanogramFilters(entry ?? '', format === '' ? null : format),
  );
  // The planogram entry shows only where the server says the shop has plans: the header's link
  // on wider layouts. A phone shows no chip beside Filters here: the index is already the
  // planograms page (Figma 7054:166079), and inside a plan the breadcrumb's Planogram crumb leads
  // back (Epic 7 3 amigos follow-ups, 2026-11-03). Whether it has plans does not depend on the
  // Format, so the last answer for this entry holds while a new Format's filters load, and the
  // entry does not blink on every pick (D-7.33-L).
  const readyAvailable = filters.status === 'ready' ? filters.value.available : undefined;
  const [lastAvailable, setLastAvailable] = useState<{
    readonly entry: string | null;
    readonly available: boolean;
  } | null>(null);
  if (
    readyAvailable !== undefined &&
    (lastAvailable?.entry !== entry || lastAvailable.available !== readyAvailable)
  ) {
    setLastAvailable({ entry, available: readyAvailable });
  }
  const available =
    readyAvailable ?? (lastAvailable?.entry === entry ? lastAvailable.available : false);

  // A new Format starts the Category afresh: the Category list is the chosen Format's.
  const choose = (nextFormat: string, nextCategory: string) =>
    navigate({ pathname: PLANOGRAMS_ROUTE, search: planogramSearch(nextFormat, nextCategory) });

  const picker = (
    <PlanogramPicker
      filters={filters.status === 'ready' ? filters.value : null}
      format={format}
      category={category}
      onFormatChange={(next) => choose(next, '')}
      onCategoryChange={(next) => choose(format, next)}
      onClear={() => choose('', '')}
    />
  );

  return (
    <div className={pageStyles.page}>
      <CatalogueLinkingHeader
        showManagerDashboard={isAdminRole(role)}
        planogramNav={available ? PLANOGRAM_NAV : undefined}
      />
      <main className={pageStyles.main} aria-label="Planograms">
        {entry === null ? (
          <div className={styles.index}>
            <p className={styles.status} role="status">
              {NO_ENTRY_MESSAGE}
            </p>
            <Link to={CHOOSE_ROUTE}>Choose a depot</Link>
          </div>
        ) : planogramId === undefined ? (
          <PlanogramIndex
            entry={entry}
            filters={filters}
            available={available}
            format={format}
            category={category}
            picker={picker}
          />
        ) : (
          <PlanogramView
            key={`${entry ?? 'no-entry'}|${planogramId}`}
            entry={entry}
            planogramId={planogramId}
            search={planogramSearch(format, category)}
            nodeId={planNode}
            onNodeChange={narrowPlan}
            picker={picker}
            phoneLayout={phoneLayout}
          />
        )}
      </main>
    </div>
  );
}

interface PlanogramPickerProps {
  readonly filters: PlanogramFilters | null;
  readonly format: string;
  readonly category: string;
  readonly onFormatChange: (format: string) => void;
  readonly onCategoryChange: (category: string) => void;
  /** Clear both, back to the page's first step. */
  readonly onClear: () => void;
}

/** Figma "Planogram bar": the Format and Category dropdowns, then "Clear filters" once either is
 *  chosen (Epic 7 3 amigos, 2026-11-02), which empties both and so brings back the prompt. */
function PlanogramPicker({
  filters,
  format,
  category,
  onFormatChange,
  onCategoryChange,
  onClear,
}: PlanogramPickerProps) {
  // A value the URL names but the list does not hold yet (or no longer) stays selectable, so
  // the select never silently shows another value than the page is using.
  const withCurrent = (values: readonly string[], current: string) =>
    current !== '' && !values.includes(current) ? [current, ...values] : values;
  const options = (values: readonly string[]) =>
    values.map((value) => ({ value, label: planogramOptionLabel(value) }));
  return (
    <div className={styles.pickerBar}>
      <div className={styles.picker}>
        <Select
          label="Format"
          placeholder="Select"
          options={options(withCurrent(filters?.formats ?? [], format))}
          value={format}
          onChange={onFormatChange}
        />
      </div>
      <div className={styles.picker}>
        <Select
          label="Category"
          placeholder="Select"
          options={options(withCurrent(filters?.categories ?? [], category))}
          value={category}
          onChange={onCategoryChange}
        />
      </div>
      {(format !== '' || category !== '') && (
        <button type="button" className={styles.clearFilters} onClick={onClear}>
          <Icon name="clockCounterClockwise" size={16} />
          Clear filters
        </button>
      )}
    </div>
  );
}

interface PlanogramIndexProps {
  readonly entry: string | null;
  readonly filters: Load<PlanogramFilters>;
  /** The server says the shop has plans on this entry. */
  readonly available: boolean;
  readonly format: string;
  readonly category: string;
  readonly picker: ReactNode;
}

/** Steps 2 and 3: the pickers, then the grid of the shop's plans in that Format and Category. */
function PlanogramIndex({
  entry,
  filters,
  available,
  format,
  category,
  picker,
}: PlanogramIndexProps) {
  const picked = format !== '' && category !== '';
  // A shop without plans is told so once: its plans are never asked for, so a pasted Format and
  // Category never add "there are no planograms here" under it.
  const unavailable = filters.status === 'ready' && !filters.value.available;
  const chosen = picked && available;
  const plans = useLoad<readonly Planogram[]>(
    entry === null || !chosen ? null : JSON.stringify([entry, format, category]),
    () => fetchPlanograms(entry ?? '', format, category),
  );

  return (
    <section className={styles.index} aria-labelledby="planograms-heading">
      {/* "Shop > Planogram", without the depot (Epic 7 3 amigos follow-ups, 2026-11-03). */}
      <nav aria-label="Breadcrumb" className={styles.crumbs}>
        <Link to="/products">Shop</Link>
        <Icon name="caretRight" size={12} />
        {/* The current crumb links back to the page's first step, so it clears the Format and
              Category too (Epic 7 3 amigos, 2026-11-02: every crumb is a link). */}
        <Link to={PLANOGRAMS_ROUTE} id="planograms-heading" aria-current="page">
          Planogram
        </Link>
      </nav>
      <div className={styles.indexBody}>
        {picker}

        {filters.status === 'error' && (
          <Banner type="error">Couldn&rsquo;t load planograms. Please try again.</Banner>
        )}
        {unavailable && (
          <p className={styles.status} role="status">
            There are no planograms for your shop on this account.
          </p>
        )}
        {filters.status !== 'error' && !unavailable && !picked && (
          <Banner type="info">{SELECT_PROMPT}</Banner>
        )}

        {picked && !unavailable && filters.status !== 'error' && plans.status === 'loading' && (
          <p className={styles.status} role="status">
            Loading planograms…
          </p>
        )}
        {chosen && plans.status === 'error' && (
          <Banner type="error">Couldn&rsquo;t load planograms. Please try again.</Banner>
        )}
        {chosen && plans.status === 'ready' && plans.value.length === 0 && (
          <p className={styles.status} role="status">
            Sorry, there are no planograms here.
          </p>
        )}
        {chosen && plans.status === 'ready' && plans.value.length > 0 && (
          <ul className={styles.grid} aria-label="Planogram layouts">
            {plans.value.map((plan) => (
              <li key={plan.planogramId}>
                <PlanogramCard plan={plan} search={planogramSearch(format, category)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** Figma "Planogram layout": the plan image with its "N Bay × M Shelf" label and an arrow. */
function PlanogramCard({ plan, search }: { readonly plan: Planogram; readonly search: string }) {
  const layout = planogramLayoutLabel(plan.bays, plan.shelves);
  return (
    <Link
      to={`${PLANOGRAMS_ROUTE}/${encodeURIComponent(plan.planogramId)}${search}`}
      className={styles.card}
    >
      <PlanogramImage src={plan.imageUrl} alt={plan.name} className={styles.cardImage} />
      <span className={styles.cardLabel}>
        <span>{layout === '' ? plan.name : layout}</span>
        <Icon name="arrowRight" size={16} />
      </span>
    </Link>
  );
}

interface PlanogramViewProps {
  readonly entry: string | null;
  readonly planogramId: string;
  /** The Format and Category the shopper came in with, as a query string. */
  readonly search: string;
  /** The Department or Category the URL narrows the plan to; null is the whole plan. */
  readonly nodeId: string | null;
  /** Narrow the plan to another node, or to the whole plan (`null`), in the URL. */
  readonly onNodeChange: (nodeId: string | null) => void;
  readonly picker: ReactNode;
  readonly phoneLayout: boolean;
}

/** Steps 4 and 5: the plan image over its products, which are ordered like any others. */
function PlanogramView({
  entry,
  planogramId,
  search,
  nodeId,
  onNodeChange,
  picker,
  phoneLayout,
}: PlanogramViewProps) {
  const plan = useLoad<Planogram | null>(entry, () => fetchPlanogram(entry ?? '', planogramId));
  const [enlarged, setEnlarged] = useState(false);
  // The node the shopper last picked, shown in the chip and the breadcrumb while its listing
  // loads. The URL's `nodeId` is the request; a pasted link or Back/Forward arrives with the id
  // alone, and the listing's answer names the node. The view is keyed by entry and plan, so it
  // starts afresh on either.
  const [picked, setPicked] = useState<CategoryNode | null>(null);
  const narrowTo = (next: CategoryNode | null) => {
    setPicked(next);
    const nextId = next?.id ?? null;
    if (nextId !== nodeId) onNodeChange(nextId);
  };
  // One scope object per plan: the listing refetches when its scope changes identity, so a new
  // object on every render (opening the image, any parent re-render) would reload the products
  // and lose the shopper's place in the plan.
  const scope = useMemo(() => ({ planogram: planogramId }), [planogramId]);

  if (plan.status === 'loading') {
    return (
      <p className={styles.status} role="status">
        Loading planogram…
      </p>
    );
  }
  if (plan.status === 'error') {
    return (
      <div className={styles.index}>
        <Banner type="error">Couldn&rsquo;t load this planogram. Please try again.</Banner>
      </div>
    );
  }
  if (plan.value === null) {
    return (
      <div className={styles.index}>
        {picker}
        <p className={styles.status} role="status">
          This planogram is not available for your shop on this account.
        </p>
        <Link to={`${PLANOGRAMS_ROUTE}${search}`}>Choose another planogram</Link>
      </div>
    );
  }

  const shown = plan.value;
  const layout = planogramLayoutLabel(shown.bays, shown.shelves);
  const title = layout === '' ? shown.name : layout;
  return (
    <>
      <CategoryListing
        node={picked !== null && picked.id === nodeId ? picked : null}
        nodeId={nodeId}
        onNavigate={narrowTo}
        scope={scope}
        // "Shop > Planogram > {plan}", then the node the plan is narrowed to: the Planogram crumb
        // takes the place of the phone's Planogram button and goes back to the grid of plans with
        // the same Format and Category, and the plan crumb to the whole plan (Epic 7 3 amigos
        // follow-ups, 2026-11-03).
        breadcrumbLeadingLinks={[
          { label: 'Shop', to: '/products' },
          { label: 'Planogram', to: `${PLANOGRAMS_ROUTE}${search}` },
        ]}
        breadcrumbRootLabel={title}
        // The plan's own words only for the whole plan: the rail's Departments are the range's,
        // so a narrowed node can hold none of the plan's products, and the ordinary copy says so.
        emptyCopy={nodeId === null ? PLAN_EMPTY_COPY : undefined}
        notFoundBackLabel="Back to the whole plan"
        header={
          <div className={styles.planHeader}>
            <div className={styles.planBar}>
              {picker}
              <button
                type="button"
                className={styles.viewFullSize}
                onClick={() => setEnlarged(true)}
              >
                View full size
                <Icon name="cornersOut" size={16} />
              </button>
            </div>
            <button
              type="button"
              className={styles.planPreview}
              onClick={() => setEnlarged(true)}
              aria-label={`View ${title} full size`}
            >
              <PlanogramImage src={shown.imageUrl} alt={shown.name} />
            </button>
          </div>
        }
      />
      <Modal
        open={enlarged}
        onClose={() => setEnlarged(false)}
        title={title}
        description={`${planogramOptionLabel(shown.format)} · ${planogramOptionLabel(shown.category)}`}
        size="wide"
        placement={phoneLayout ? 'bottom' : 'center'}
        headerStyle="divided"
      >
        <PlanogramImage src={shown.imageUrl} alt={shown.name} className={styles.enlarged} />
        {phoneLayout && <p className={styles.zoomHint}>Pinch to zoom</p>}
      </Modal>
    </>
  );
}

export default Planograms;
